import { useQueries } from "@tanstack/react-query";
import { useContext } from "react";

import { TenantContext } from "@nens/ranalib";
import { removeNullUndefined } from "@nens/shared-utils";

import { FileDataTypeEnum } from "@/lib/rana-file-data-type";
import { getPublicationFileVectorStyleQuery } from "@/queries/query-style-sprite";
import {
  SpriteSpec,
  getVectorDescriptorSprite,
} from "@/utils/get-vector-descriptor-layers-and-sprite";

import { getFiles, getLayersOfType } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";

export function useVectorDescriptorSprites(): { sprites: SpriteSpec[]; isLoading: boolean } {
  const { tenant } = useContext(TenantContext);

  const allLayers = usePublicationStore(getLayersOfType(FileDataTypeEnum.Vector));
  const files = usePublicationStore(getFiles);
  const items = removeNullUndefined(
    allLayers.map((layer) => {
      const file = files.find((file) => file.path === layer.filePath);
      if (!file) return null;
      const descriptorId = file.file.descriptor.id;
      return { descriptorId, layer };
    })
  );

  const uniqueStyles = items.filter(
    ({ descriptorId, layer }, idx, arr) =>
      arr.findIndex(
        (item) =>
          item.descriptorId === descriptorId &&
          item.layer.properties.styleId === layer.properties.styleId
      ) === idx
  );

  const vectorStyleQueries = useQueries({
    queries: uniqueStyles.map(({ descriptorId, layer }) =>
      getPublicationFileVectorStyleQuery(descriptorId, layer.properties.styleId)
    ),
  });

  const sprites = removeNullUndefined(
    uniqueStyles.map(({ descriptorId, layer }, index) => {
      const styleJson = vectorStyleQueries[index]?.data;
      if (!descriptorId || !styleJson) return null;

      return getVectorDescriptorSprite({
        descriptorId,
        styleId: layer.properties.styleId,
        tenant,
        styleJson,
      });
    })
  );

  const isLoading = vectorStyleQueries.some((q) => q.isLoading);
  return { sprites, isLoading };
}
